import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DownloadOutlined, SaveOutlined } from '@ant-design/icons';
import { App, Badge, Button, Form, Spin, Tabs, Tooltip } from 'antd';

import AddEquipmentTab from './Tabs/AddEquipmentTab/AddEquipmentTab';
import CompanyDetailsTab from './Tabs/CompanyDetailsTab/CompanyDetailsTab';
import LaborTab from './Tabs/LaborTab/LaborTab';
import ReviewTab from './Tabs/ReviewTab/ReviewTab';
import SummaryTab from './Tabs/SummaryTab/SummaryTab';

import { Tab } from '@/utils/tabs';
import { useMutation, useQuery as useReactQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useAuth } from '../../../context/AuthProvider';
import { setCompanies } from '../../../contextRedux/Company/CompanyStore';
import { setDevices as setDeviceTypes } from '../../../contextRedux/DeviceType/DeviceTypeStore';
import { setLaborExtras } from '../../../contextRedux/LaborExtra/LaborExtraStore';
import { setPmOfferContractTypes } from '../../../contextRedux/PmOfferContractTypes/PmOfferContractTypesStore';
import {
  setPmOfferProjects,
  updateEquipmentTab,
  updateField,
} from '../../../contextRedux/PMOfferProject/PmOfferModelProject';
import { setPmOfferStatuses } from '../../../contextRedux/PmOfferStatus/PmOfferStatusStore';
import { setSystemTypes } from '../../../contextRedux/SystemType/SystemTypeStore';
import { setUnitMeasures } from '../../../contextRedux/UnitMeasure/UnitMeasureStore';
import { setUsers } from '../../../contextRedux/User/UserStore';
import PmOfferStatus from '../../../enums/PmOfferStatus';
import CompanyService from '../../../services/company/CompanyService';
import ConfigurationService from '../../../services/configuration/ConfigurationService';
import DeviceTypeService from '../../../services/deviceType/DeviceTypeService';
import LaborExtraService from '../../../services/laborExtra/LaborExtraService';
import PmOfferService from '../../../services/pmOffer/PmOfferService';
import SystemTypeService from '../../../services/systemType/SystemTypeService';
import UserService from '../../../services/user/UserService';
import { RootState } from '../../../store';
import RaportTab from './Tabs/RaportTab/RaportTab';
//import CommentsTab from './Tabs/CommentsTab/CommentsTab';

const saveBlobCrossPlatform = (blob: Blob, filename: string) => {
  const w = window as any;
  if (w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function') {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const base64 = (reader.result as string).split(',')[1];
        w.AndroidInterface.saveBlobFile(base64, filename);
      } catch {}
    };
    reader.readAsDataURL(blob);
    return;
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const NewPmOfferProject: FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 720 });
  const { message } = App.useApp();
  // const isTab = useMediaQuery({ maxWidth: 920});

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQuery();

  const [form] = Form.useForm();
  const [id, setId] = useState<string | null>(query.get('id'));
  const reduxPmOffer = useSelector((state: RootState) => state?.models.pmOfferProject);
  const addedDevices = useSelector((state: RootState) => state.models.pmOfferProject.pmOfferWorkPlaceItems);

  let totalOfferValue =
    Number.parseFloat(
      (
        reduxPmOffer.pmOfferWorkPlaceItems?.reduce((total: any, item: any) => total + item.laborPrice, 0) +
        (reduxPmOffer.pmOfferLaborExtras
          ? reduxPmOffer.pmOfferLaborExtras?.reduce((total: any, item: any) => total + item?.piece * item?.price, 0)
          : 0)
      ).toFixed(2)
    ) +
    Number.parseFloat(
      reduxPmOffer.pmOfferWorkPlaceItems
        ?.reduce((total: any, item: any) => {
          const totalPrice = item.price;
          return total + totalPrice;
        }, 0)
        .toFixed(2)
    );
  const { profile } = useAuth();
  const userRols = 'sales, tehnician, senior_tehnician, dispatcher';

  const isShowCompanyDetailsTab: boolean = !!(
    reduxPmOffer.companyId &&
    reduxPmOffer.accountManagerId &&
    reduxPmOffer.projectAuthorId &&
    reduxPmOffer.enumPmOfferContractTypeId &&
    reduxPmOffer.enumPmOfferStatusId &&
    reduxPmOffer.date &&
    reduxPmOffer.dateStart &&
    reduxPmOffer.dateEnd
  );

  const canSavePmOffer =
    addedDevices?.some((item: any) => item.addition < 50) && reduxPmOffer.enumPmOfferStatusId != PmOfferStatus.Aprobata;

  const { data: _accountSales, isFetching: isUsersLoading } = useReactQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: userRols,
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const idFromUrl = query.get('id');
    if (!idFromUrl) {
    }
    setId(idFromUrl);
  }, [location.search]);

  const { data: _pmOffer, isFetching: isPmOfferLoading } = useReactQuery({
    queryKey: ['pmOffers'],
    queryFn: () =>
      PmOfferService.search({
        ids: [id!],
        isActive: true,
        isStatusIncluded: true,
        isTypeIncluded: true,
        isCompanyIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
        isPmOfferWorkPlaceIncluded: true,
        isLaborExtraIncluded: true,
        isFilesIncluded: true,
      }).then((data) => {
        dispatch(setPmOfferProjects(data?.response[0]));
        return data?.response[0];
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _companies, isFetching: isCompaniesLoading } = useReactQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAccountManagerIncluded: true,
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _status, isFetching: isStatusLoading } = useReactQuery({
    queryKey: ['pmOfferStatuses'],
    queryFn: () =>
      ConfigurationService.searchPmOfferStatus({}).then((data) => {
        dispatch(setPmOfferStatuses(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _offerTypes, isFetching: isOfferTypesLoading } = useReactQuery({
    queryKey: ['pmOfferContractTypes'],
    queryFn: () =>
      ConfigurationService.searchPmOfferContractType({}).then((data) => {
        dispatch(setPmOfferContractTypes(data?.response?.filter((pc: any) => [2].includes(pc.id))));
        return data?.response?.filter((pc: any) => [2].includes(pc.id));
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _unitMeasures, isFetching: isUnitMeasuresLoading } = useReactQuery({
    queryKey: ['unitMeasures'],
    queryFn: () =>
      ConfigurationService.searchConfiguration({ tag: 'unitMeasure' }).then((data) => {
        dispatch(setUnitMeasures(data?.response?.section));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _laborExtras, isFetching: isLaborExtrasLoading } = useReactQuery({
    queryKey: ['laborExtras'],
    queryFn: () =>
      LaborExtraService.search({ isActive: true }).then((data) => {
        dispatch(setLaborExtras(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _systemTypes, isFetching: isSystemTypesLoading } = useReactQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setSystemTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _deviceTypes, isFetching: isDeviceTypesLoading } = useReactQuery({
    queryKey: ['deviceTypes'],
    queryFn: () =>
      DeviceTypeService.search({
        isSystemTypeIncluded: true,
        isLaborIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setDeviceTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _pmOfferComments,
    refetch: searchComments,
    isFetching: isPmOfferCommentsLoading,
  } = useReactQuery({
    queryKey: ['pmOfferComment'],
    queryFn: () =>
      PmOfferService.searchComment({
        ids: [id!],
      }).then((data) => {
        const departments = ['general', 'contabilitate', 'manager', 'instalatori'];

        // Initialize offerComments with all departments
        const initialOfferComments = departments.map((departmentName) => {
          const existingComment = data?.response?.find((comment: any) => comment.departmentName === departmentName);
          return (
            existingComment || {
              id: '',
              pmOfferId: id,
              departmentName,
              message: '',
              pmOfferCommentFiles: [],
            }
          );
        });
        dispatch(updateField({ field: 'pmOfferCommentsTab', value: initialOfferComments }));
        return data?.response;
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { mutate: savePmOffer, isPending: isSavingPmOffer } = useMutation({
    mutationFn: (formData: any) => PmOfferService.upsert(id, formData),
    onSuccess: (data: any) => {
      if (data && typeof data === 'object') {
        handleResponse(data);

        if (data?.response?.id) {
          navigate(`/project-management/pmOfferProject/edit?id=${data?.response?.id}`);
        }

        if (id) {
          searchComments(); // Ensure searchComments handles empty or null results correctly
        }
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  const onSaveOffer = () => {
    savePmOffer({
      ...reduxPmOffer,
      pmOfferComments: reduxPmOffer.pmOfferCommentsTab,
      pmOfferIntervals: reduxPmOffer.pmOfferIntervals,
      pmOfferSectionFiles: reduxPmOffer.pmOfferSectionFiles ?? [],
    });
  };

  const handleSaveOffer = async () => {
    try {
      await onSaveOffer();
    } catch (errorInfo: any) {
      message.error(errorInfo?.errorFields[0]?.errors);
    }
  };

  const handleResponse = (data: any) => {
    if (data?.errors?.length > 0) {
      data?.errors?.forEach((item: any) => {
        message.error(item);
      });
    } else {
      message.success(
        <span>
          <span className='font-weight-bold'>{data?.response?.number}</span> a fost {!!id ? 'modificat' : 'adaugat'} cu
          success
        </span>
      );
    }
  };

  const { mutate: viewPdfPmOffer, isPending: isViewingPdfPmOffer } = useMutation({
    mutationFn: (formData: any) => PmOfferService.preview(id, formData),
    onSuccess: (data: any) => {
      if (!data || !data.blob) {
        throw new Error('Invalid response received from the server');
      }
      return data.blob().then((blob: any) => {
        const url = window.URL.createObjectURL(blob);
        dispatch(
          updateEquipmentTab({
            field: 'pdfUrl',
            value: url,
          })
        );
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const viewPdf = () => {
    if (isShowCompanyDetailsTab) {
      viewPdfPmOffer(reduxPmOffer);
    }
  };

  const { mutate: printPdfPmOffer, isPending: isPrintingPmOffer } = useMutation({
    mutationFn: () =>
      PmOfferService.print({
        ids: [id!],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isPmOfferWorkPlaceIncluded: true,
        isLaborExtraIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }),
    onSuccess: (response: any) => {
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const tabs: (Tab | false)[] = [
    {
      key: 'companyTab',
      label: 'Detalii Client',
      children: <CompanyDetailsTab form={form} />,
    },
    isShowCompanyDetailsTab && {
      key: 'deviceTab',
      label: 'Adaugare Continut Oferta',
      children: <AddEquipmentTab />,
    },
    addedDevices?.length! > 0 && {
      key: 'summaryTab',
      label: (
        <>
          Vizualizare Echipamente Adaugate
          <Badge
            color='blue'
            style={{ marginLeft: '5px' }}
            count={addedDevices?.length! > 0 ? addedDevices?.length : ''}
          ></Badge>
        </>
      ),
      children: <SummaryTab />,
    },
    addedDevices?.length! > 0 && {
      key: 'manoperaTab',
      label: 'Manopera',
      children: <LaborTab />,
    },
    // addedDevices?.length! > 0 && {
    //   key: 'commentsTab',
    //   label: 'Observatii si Comentarii',
    //   children: <CommentsTab />,
    // },
    addedDevices?.length! > 0 &&
      isShowCompanyDetailsTab && {
        key: 'reviewTab',
        label: 'Review',
        children: <ReviewTab isViewingPdfPmOffer={isViewingPdfPmOffer} />, // Use key to force re-render
      },
    addedDevices?.length! > 0 &&
      isShowCompanyDetailsTab && {
        key: 'raportTab',
        label: 'Raport',
        children: <RaportTab />, // Use key to force re-render
      },
  ];

  const filteredTabs: Tab[] = tabs.filter((tab): tab is Tab => tab !== false);

  if (
    isSavingPmOffer ||
    isPrintingPmOffer ||
    isUsersLoading ||
    isPmOfferLoading ||
    isCompaniesLoading ||
    isStatusLoading ||
    isLaborExtrasLoading ||
    isOfferTypesLoading ||
    isUnitMeasuresLoading ||
    isSystemTypesLoading ||
    isDeviceTypesLoading ||
    isPmOfferCommentsLoading
  ) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <>
      <div className='topLine'>
        <h2>{id ? 'Oferta Proiect - ' + reduxPmOffer.number : 'Oferta Noua Proiect'}</h2>

        <div className='totalLine'>
          <h3>{'Total - ' + totalOfferValue + ' lei'}</h3>
          {addedDevices?.some((item: any) => item.addition < 50)! && (
            <h3 style={{ color: 'red' }}>Adaosul produselor s-a modificat sub 50%</h3>
          )}

          <div className='buttons'>
            <Button
              onClick={() => handleSaveOffer()}
              className='saveBtn'
              disabled={
                profile?.userPermission?.rols?.some((role: any) => role == 'senior_sales') ? false : canSavePmOffer
              }
            >
              {isMobile ? (
                <Tooltip title='Salveaza Oferta'>
                  <SaveOutlined />
                </Tooltip>
              ) : (
                'Salveaza Oferta'
              )}
            </Button>

            {id!! && addedDevices?.length! > 0 && (
              <Button
                className='downloadBtn'
                onClick={() => printPdfPmOffer()}
                disabled={
                  profile?.userPermission?.rols?.some((role: any) => role == 'senior_sales') ? false : canSavePmOffer
                }
              >
                {isMobile ? (
                  <Tooltip title='Descărcare PDF'>
                    <DownloadOutlined />
                  </Tooltip>
                ) : (
                  <>
                    {' '}
                    <DownloadOutlined /> Descărcare PDF{' '}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Tabs
        defaultActiveKey='1'
        items={filteredTabs}
        onChange={(key) => {
          if (key === 'reviewTab') {
            viewPdf(); // Increment the key to force re-render
          }
        }}
        // tabPosition={isMobile || isTab ? 'top' : 'left'}
        tabBarStyle={{ backgroundColor: '#DADBDD' }}
        tabBarGutter={18}
      />
    </>
  );
};

export default NewPmOfferProject;
